import {ReactNode, useEffect, useMemo, useState} from 'react';
import {InvenTypeVo} from '../../../../common/shared/api/gateway/enums/InvenTypeVo.enum';
import {
  InvenListInput,
  SearchInvenListInput,
} from '../../../../graphql/generated/types';
import {ReactQueryInfiniteType} from '../../../../reactQuery/CodegenReactQueryType.type';
import {ReactQueryInfiniteFetcherChildren} from '../../../../reactQuery/ReactQueryInfiniteFetcherChildren';
import {ALL_CATEGORY_UID, REFETCH_INVEN_EVENT_NAME} from '../constant';
import {useInfiniteInvenListQuery} from '../hook/useInfiniteInvenListQuery';
import {InvenListInfiniteQueryProps} from '../type';

export type InvenListFetcherHeadlessProps =
  | InvenListProps
  | SearchInvenListProps;

export function InvenListFetcherHeadless({
  children,
  initialInvenType,
  initialSortType,
  initialCategoryUid,
  isEditPage,
  isSearch,
  word,
}: InvenListFetcherHeadlessProps) {
  // 쿼리 키 변경을 위한 useState 사용합니다.
  const [invenType, setInvenType] = useState(initialInvenType);
  const [sortType, setSortType] = useState(
    initialSortType ?? DEFAULT_SORT_TYPE[invenType]
  );
  const [categoryUid, setCategoryUid] = useState(initialCategoryUid);

  // 쿼리 키가 변경되면 자동으로 리패칭 합니다.
  const queryKey = useMemo(
    () => ({
      invenListInput: {
        page: 0,
        size: 25,
        sortType,
        categoryUid,
        isEditPage,
      },
    }),
    [sortType, categoryUid, isEditPage]
  );
  const searchQueryKey = useMemo(
    () => ({
      searchInvenListInput: {
        page: 0,
        sortType,
        categoryUid: categoryUid ?? ALL_CATEGORY_UID,
        word,
        type: invenType,
      },
    }),
    [sortType, categoryUid, word, invenType]
  );

  const {
    isFetching,
    isLoading,
    data,
    refetch,
    fetchNextPage,
    fetchPreviousPage,
  } = useInfiniteInvenListQuery({
    invenType,
    queryKey,
    searchQueryKey,
    isSearch,
    sortType,
    categoryUid,
    options: {
      refetchOnMount: true,
    },
  });

  useEffect(() => {
    if (!invenType) return;

    function onRefetch() {
      refetch();
      window.scrollTo(0, 0);
    }
    window.addEventListener(REFETCH_INVEN_EVENT_NAME, onRefetch);
    return () => {
      window.removeEventListener(REFETCH_INVEN_EVENT_NAME, onRefetch);
    };
  }, [invenType, refetch]);

  // invenType 이 바뀌면 state 변수 초기화
  useEffect(() => {
    setInvenType(initialInvenType);
    setSortType(initialSortType ?? DEFAULT_SORT_TYPE[initialInvenType]);
    setCategoryUid(initialCategoryUid);
  }, [initialInvenType, initialSortType, initialCategoryUid]);

  return (
    <ReactQueryInfiniteFetcherChildren
      type={ReactQueryInfiniteType.InfinityScroll}
      showInfiniteLoader={false}
      data={data}
      isLoading={isLoading}
      isFetching={isFetching}
      fetchNextPage={fetchNextPage}
      fetchPreviousPage={fetchPreviousPage}
    >
      {data =>
        children(
          data,
          invenType,
          {queryKey, searchQueryKey},
          setSortType,
          setCategoryUid,
          sortType,
          categoryUid
        )
      }
    </ReactQueryInfiniteFetcherChildren>
  );
}

const DEFAULT_SORT_TYPE: Record<InvenTypeVo, string> = {
  [InvenTypeVo.Recent]: 'recent',
  [InvenTypeVo.Paid]: 'paid',
  [InvenTypeVo.Like]: 'like',
};

type InvenListFetchChildren = (
  data: InvenListInfiniteQueryProps['data'],
  invenType: InvenTypeVo,
  keys: {
    queryKey: any;
    searchQueryKey: any;
  },
  setSortType: (sortType: string) => void,
  setCategoryUid: (categoryUid?: number) => void,
  sortType: string,
  categoryUid?: number
) => ReactNode;

interface InvenListFetcherCommonProps {
  children: InvenListFetchChildren;
  initialInvenType: InvenTypeVo;
  initialSortType?: string;
  initialCategoryUid?: number;
}

interface InvenListProps
  extends InvenListFetcherCommonProps,
    Pick<InvenListInput, 'isEditPage'> {
  isSearch?: never;
  word?: never;
}

interface SearchInvenListProps
  extends InvenListFetcherCommonProps,
    Pick<SearchInvenListInput, 'word'> {
  isSearch: true;
  isEditPage?: never;
}
